import { useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { CONTACT } from "@/lib/copy";
import { scrollToId } from "@/lib/scroll";
import { staticPath, pathFor } from "@/lib/siteMap";
import { track } from "@/lib/analytics";
import { Wordmark } from "./Navbar";
import { useTheme } from "@/lib/theme";
import { Facebook, Instagram, Linkedin, Youtube, Phone, Mail } from "lucide-react";
import { TikTokIcon } from "./icons";

export default function Footer() {
  const { t, lang } = useLanguage();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const L = `/${lang}`;
  const isHome = location.pathname === `${L}/` || location.pathname === L;

  const menu = [
    { label: t.ui.home, to: `${L}/` },
    { label: t.ui.services, to: staticPath(lang, "servicios") },
    { label: t.ui.pricing, to: staticPath(lang, "precios") },
    { label: t.nav.marketing, to: pathFor(lang, "agencia-marketing") },
    { label: t.ui.work, action: () => (isHome ? scrollToId("casos") : navigate(`${L}/#casos`)) },
    { label: t.ui.contact, to: staticPath(lang, "contacto") },
  ];
  const socials = [
    { icon: Instagram, href: CONTACT.instagram, label: "Instagram" },
    { icon: Facebook, href: CONTACT.facebook, label: "Facebook" },
    { icon: Linkedin, href: CONTACT.linkedin, label: "LinkedIn" },
    { icon: Youtube, href: CONTACT.youtube, label: "YouTube" },
    { icon: TikTokIcon, href: CONTACT.tiktok, label: "TikTok" },
  ];

  return (
    <footer className="relative border-t border-white/5" data-testid="footer">
      <div className="mx-auto max-w-7xl px-6 pt-20">
        <div className="grid grid-cols-1 gap-12 pb-20 sm:grid-cols-2 md:grid-cols-12">
          <div className="md:col-span-4">
            <Wordmark className="text-xl" />
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-zinc-400">{t.footer.tagline}</p>
            <p className="mt-3 max-w-sm font-mono text-[11px] uppercase tracking-[0.18em] text-volt" data-testid="footer-chile">
              {t.footer.chile}
            </p>
            <p className="mt-3 inline-flex items-center gap-2 rounded-full border border-volt/30 bg-volt/10 px-3.5 py-1.5 text-xs text-zinc-300" data-testid="footer-guarantee">
              <span className="h-1.5 w-1.5 rounded-full bg-volt" aria-hidden="true" />
              {t.footer.guarantee}
            </p>
            <div className="mt-6 space-y-2.5">
              <a
                href={`tel:${CONTACT.whatsapp.replace(/\s/g, "")}`}
                data-testid="footer-phone"
                className="flex items-center gap-2.5 text-sm text-zinc-400 transition-colors duration-300 hover:text-white"
              >
                <Phone className="h-3.5 w-3.5 text-volt" />
                {CONTACT.whatsapp}
              </a>
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="footer-email"
                className="flex items-center gap-2.5 text-sm text-zinc-400 transition-colors duration-300 hover:text-white"
              >
                <Mail className="h-3.5 w-3.5 text-volt" />
                {CONTACT.email}
              </a>
            </div>
          </div>
          <div className="md:col-span-2">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400">{t.footer.menu}</p>
            <ul className="mt-4 space-y-3">
              {menu.map((m) => (
                <li key={m.label}>
                  <button
                    onClick={() => (m.action ? m.action() : navigate(m.to))}
                    className="text-sm text-zinc-400 transition-colors duration-300 hover:text-white"
                    data-testid={`footer-link-${m.label.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    {m.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400">{t.footer.locations}</p>
            <ul className="mt-4 space-y-3">
              {t.footer.cities.map((city) => (
                <li key={city} className="flex items-center gap-2.5" data-testid={`footer-city-${city.toLowerCase().replace(/\s/g, "-")}`}>
                  <img src="/assets/logo-mark.png" alt="" aria-hidden="true" className="h-3.5 w-auto opacity-90" style={{ filter: theme.logoFilter }} />
                  <span className="text-sm text-zinc-400">{city}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400">{t.footer.social}</p>
            <div className="mt-4 flex items-center gap-3">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  data-testid={`footer-social-${s.label.toLowerCase()}`}
                  onClick={() => track(`click_${s.label.toLowerCase()}`, { language: lang })}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-zinc-400 transition-colors duration-300 hover:border-volt/60 hover:text-volt"
                >
                  <s.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="select-none overflow-hidden px-2" aria-hidden="true">
        <p className="text-center font-display text-[13.5vw] font-medium leading-[0.85] tracking-tight text-white/[0.05]">
          DEVOLUZION
        </p>
      </div>

      <div className="border-t border-white/5">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-6 py-6 sm:flex-row">
          <p className="font-mono text-[10px] tracking-[0.2em] text-zinc-400">
            © 2026 DEVOLUZION — {t.footer.rights}
          </p>
          <p className="font-mono text-[10px] tracking-[0.2em] text-zinc-700">
            IDEA → DISEÑO → CÓDIGO → IA → MARKETING → GROWTH
          </p>
        </div>
      </div>
    </footer>
  );
}
